import React, { useState, useEffect } from 'react'; // Dodano useState i useEffect
import { Info, CheckCircle, AlertTriangle } from 'lucide-react';
import './Popup.css'; // Założenie, że style zostaną przeniesione lub dostosowane

// --- Komponent Popup ---
// Renderuje się zawsze, ale klasa 'visible' kontroluje widoczność i animację
const Popup = ({ config, onClose }) => {
  // Log the received config for debugging
  // console.log("Popup received config:", config); // Można odkomentować w razie potrzeby

  // Sprawdzamy, czy config jest prawidłowym obiektem i czy ma wymagane pola
  const isValidConfig = config && (typeof config === 'object') && (config.message || config.title);

  // Jeśli nie ma config lub jest nieprawidłowy, ustawiamy wartości domyślne, aby uniknąć błędów
  // Ensure 'type' is correctly extracted or defaulted
  const {
    title,
    message,
    type = 'info',
    size = 'medium', // Dodajemy 'size' z domyślną wartością 'medium'
    confirmText = 'OK',
    cancelText = 'Anuluj',
    onConfirm,
    onCancel,
    showInput = false, // Przywrócono i odczytujemy z config
    inputPlaceholder = '',
    initialInputValue = '',
    inputType = 'text'
  } = isValidConfig ? config : {};

  // Stan dla wartości pola input
  const [inputValue, setInputValue] = useState(''); // Zawsze inicjuj pustym stringiem

  // Resetuj inputValue, gdy zmienia się config (np. otwiera się nowy popup)
  useEffect(() => {
    if (isValidConfig) {
      // Używamy initialInputValue z configu, jeśli jest dostępny
      setInputValue(config?.initialInputValue !== undefined ? String(config.initialInputValue) : ''); // Konwertuj na string dla pewności
    } else {
      setInputValue(''); // Reset if config becomes invalid
    }
    // Zależność tylko od config, isValidConfig jest pochodną
  }, [config]);

  const handleOverlayClick = (e) => {
    // Zamykaj tylko jeśli kliknięto na overlay, a nie na content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    // console.log(`Popup handleConfirm called. Type: ${type}, InputValue: ${inputValue}`); // Można odkomentować
    // Przekaż wartość inputu do onConfirm, jeśli jest on widoczny
    if (onConfirm) {
      // Pass inputValue if the input field was shown, otherwise pass undefined
      const valueToPass = (type === 'input' || showInput) ? inputValue : undefined;
      // console.log("Calling onConfirm with value:", valueToPass); // Można odkomentować
      onConfirm(valueToPass);
    } else {
      // console.log("No onConfirm callback provided."); // Można odkomentować
    }
    // Close the popup after handling confirm
     onClose();
  };

  const handleCancel = () => {
    // console.log("Popup handleCancel called."); // Można odkomentować
    if (onCancel) onCancel();
    onClose();
  };

  const Icon = {
    info: Info,
    success: CheckCircle,
    error: AlertTriangle,
    confirm: AlertTriangle, // Można użyć innej ikony dla potwierdzenia
    loading: () => <div className="popup-spinner"></div> // Prosty spinner CSS
  }[type];

  // Add 'visible' class only when config is valid
  const overlayClass = `popup-overlay ${isValidConfig ? 'visible' : ''}`;

  return (
    <div className={overlayClass} onClick={handleOverlayClick}>
      {/* Renderuj zawartość tylko jeśli popup ma być widoczony (config istnieje i jest prawidłowy), aby uniknąć błędów */}
      {isValidConfig && (
        <div className={`popup-content popup-${type} popup-size-${size}`}>
          <div className="popup-header">
            {Icon && <Icon size={24} className="popup-icon" />}
            <h3>{title || (type === 'error' ? 'Błąd' : type === 'success' ? 'Sukces' : 'Informacja')}</h3>
            <button onClick={onClose} className="popup-close-btn">&times;</button>
          </div>
          <div className="popup-body">
            {typeof message === 'string' ? <p>{message}</p> : message}
            {/* Render input field if type is 'input' or showInput is true */}
            {(type === 'input' || showInput) && (
              <input
                type={inputType} // Use inputType from props
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={inputPlaceholder}
                className="popup-input" // Dodaj klasę do stylizacji
                autoFocus // Automatyczne ustawienie fokusa
              />
            )}
          </div>
          <div className="popup-footer">
            {/* Footer buttons logic */}
            {(() => {
              if (type === 'loading') {
                return null; // No buttons for loading
              }
              if (type === 'confirm' || type === 'input' || showInput) {
                // Show Confirm and Cancel for 'confirm', 'input' and 'showInput' types
                return (
                  <>
                    <button onClick={handleConfirm} className="popup-btn popup-btn-confirm">
                      {confirmText}
                    </button>
                    <button onClick={handleCancel} className="popup-btn popup-btn-cancel">
                      {cancelText}
                    </button>
                  </>
                );
              }
              // Default for info, success, error: OK button that closes
              return (
                 <button onClick={onClose} className="popup-btn popup-btn-confirm">
                   {confirmText} {/* Use confirmText as OK button text */}
                 </button>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Popup;
